import { AxiosError } from 'axios';
import { API_ENDPOINTS } from '@/config/api.config.ts';
import { logger } from '@/services/logger.service.ts';
import { navigateTo } from '@/utils/navigation.utils.ts';
import { adminClient } from './adminClient';
import {
  clearPersistedTokens,
  readPersistedRefreshToken,
  writePersistedAccessToken,
  writePersistedRefreshToken,
} from '../auth/tokenStorage';
import { useAuthStore } from '../auth/useAuthStore';
import { usePermStore } from '../auth/usePermStore';
import type {
  AdminRoleRef,
  AdminUser,
  BackendPending,
  MeResponse,
} from '../auth/adminTypes';

export interface AdminLoginDto {
  email?: string;
  username?: string;
  password: string;
}

/** Backend-real identity payloads: login `user` + flat `me/permissions`
 * (`{ user_id, role, staff_disabled, perm_version, staff_roles, permissions }`). */
interface BackendLoginUser {
  user_id?: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  status?: string;
  staff_disabled?: boolean;
}

interface LoginResponse {
  user?: BackendLoginUser;
  admin?: Omit<AdminUser, 'role'> & { role?: AdminRoleRef };
  access_token: string;
  refresh_token?: string;
  expires_in?: string | number;
}

/** Map the backend-flat user to the store AdminUser (tolerates both `user` and `admin` keys). */
function toAdminUser(raw: BackendLoginUser | LoginResponse['admin']): AdminUser {
  if (!raw || typeof raw !== 'object') {
    throw new Error(GENERIC_LOGIN_ERROR);
  }
  if ('user_id' in raw || 'staff_disabled' in raw || 'staff_roles' in raw) {
    const u = raw as BackendLoginUser & { staff_roles?: string[] };
    const roles = Array.isArray(u.staff_roles) ? u.staff_roles : [];
    // staff_roles is authoritative (e.g. ["super_admin"] beats role: "admin").
    const roleName =
      roles.includes('super_admin')
        ? 'super_admin'
        : typeof u.role === 'string' && u.role
          ? u.role
          : roles[0] ?? 'staff';
    const status: AdminUser['status'] =
      u.staff_disabled === true || u.status === 'disabled' ? 'disabled' : 'active';
    const email = typeof u.email === 'string' ? u.email : '';
    const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || email || roleName;
    return {
      id: u.user_id !== undefined && u.user_id !== null ? String(u.user_id) : email || roleName,
      email,
      fullName,
      status,
      role: { id: roleName, name: roleName, isSystem: roleName === 'super_admin' },
    };
  }
  const a = raw as Omit<AdminUser, 'role'> & { role?: AdminRoleRef };
  if (!a || typeof a.email !== 'string') {
    throw new Error(GENERIC_LOGIN_ERROR);
  }
  return { ...a, role: a.role ?? { id: '', name: '', isSystem: false } };
}

const GENERIC_LOGIN_ERROR = 'Invalid credentials';

/**
 * Persisted tokens (localStorage) — see `./tokenStorage`.
 * Both tokens survive reloads + new tabs. Deliberately NOT among the purged
 * legacy keys (`token`, `refresh_token`, `user`).
 */
const readStoredRefreshToken = readPersistedRefreshToken;
const writeStoredRefreshToken = writePersistedRefreshToken;

function isPendingStatus(status?: number): boolean {
  return status === 404 || status === 501;
}

function toBackendPending(): BackendPending {
  return { pending: true };
}

/** Fetch `GET /api/admin/staff/me/permissions` → hydrate auth + perm stores. 404/501 → graceful pending fallback. */
export async function fetchMe(): Promise<MeResponse | BackendPending> {
  const { setSession, setStatus } = useAuthStore.getState();
  const { setPerms } = usePermStore.getState();
  useAuthStore.setState({ status: 'loading' });
  try {
    const res = await adminClient.get<MeResponse>(API_ENDPOINTS.admin.mePermissions);
    const { permissions } = res.data;
    const shaped = res.data as MeResponse & { user?: BackendLoginUser };
    const raw = shaped.user ?? (res.data as MeResponse).admin ?? res.data;
    const token = useAuthStore.getState().accessToken ?? '';
    const refreshToken = useAuthStore.getState().refreshToken;
    const parsed = toAdminUser(raw);
    // The flat shape carries no email/name — keep the richer login identity.
    const prev = useAuthStore.getState().admin;
    if (!parsed.email && prev?.email) parsed.email = prev.email;
    if ((!parsed.fullName || parsed.fullName === parsed.role.name) && prev?.fullName) {
      parsed.fullName = prev.fullName;
    }
    setSession(token, parsed, refreshToken);
    setPerms(permissions ?? []);
    if (parsed.status === 'disabled') {
      setStatus('disabled');
    }
    return res.data;
  } catch (error) {
    const status = (error as AxiosError).response?.status;
    if (isPendingStatus(status)) {
      // Route missing ≠ logged out: keep prior status (boot guard decides).
      return toBackendPending();
    }
    if (status === 401 || status === 403) {
      // Interceptor already redirected to login.
      setStatus('anon');
      return toBackendPending();
    }
    // Network flakes etc: keep the session, let the caller decide.
    throw error;
  }
}

/** R1 login: memory-token session + `me` hydration. Generic error only. */
export async function login(dto: AdminLoginDto): Promise<MeResponse | BackendPending> {
  const { setSession } = useAuthStore.getState();
  const { setPerms } = usePermStore.getState();
  useAuthStore.setState({ status: 'loading' });
  try {
    // Backend accepts the identity as username OR email (legacy contract used username).
    const identity = dto.username ?? dto.email ?? '';
    const res = await adminClient.post<LoginResponse>(API_ENDPOINTS.admin.login, {
      username: identity,
      email: identity,
      password: dto.password,
    });
    const { access_token, refresh_token } = res.data;
    if (!access_token) {
      throw new Error(GENERIC_LOGIN_ERROR);
    }
    const raw = res.data.user ?? res.data.admin;
    // `me` is authoritative for role + permissions; login identity may be partial.
    setSession(access_token, toAdminUser(raw), refresh_token ?? null);
    writeStoredRefreshToken(refresh_token ?? null);
    try {
      const me = await fetchMe();
      return me;
    } catch {
      // Login succeeded but `me` probe failed (backend pending): keep session.
      setPerms([]);
      useAuthStore.setState({ status: 'authed' });
      return toBackendPending();
    }
  } catch (error) {
    useAuthStore.setState({ status: 'anon' });
    logger.debug('Admin login failed');
    throw new Error(GENERIC_LOGIN_ERROR);
  }
}

/**
 * R2 refresh via body refresh_token. Falls back to the persisted token so
 * reloads / new tabs can silently re-authenticate. Returns new token or null.
 */
export async function refresh(): Promise<string | null> {
  const refresh_token =
    useAuthStore.getState().refreshToken ?? readStoredRefreshToken();
  if (!refresh_token) return null;
  try {
    const res = await adminClient.post<{ access_token: string; refresh_token?: string }>(
      API_ENDPOINTS.admin.refresh,
      { refresh_token },
    );
    const token = res.data?.access_token;
    if (token) {
      const rotated = res.data?.refresh_token ?? refresh_token;
      useAuthStore.setState({ accessToken: token, refreshToken: rotated });
      writePersistedAccessToken(token);
      writeStoredRefreshToken(rotated);
      return token;
    }
    return null;
  } catch {
    return null;
  }
}

/** R3 logout: revoke server session, clear memory + persisted tokens, redirect to admin login. */
export async function logout(next?: string): Promise<void> {
  try {
    await adminClient.post(API_ENDPOINTS.admin.logout, {});
  } catch (error) {
    logger.debug('Admin logout endpoint failed, clearing locally');
  } finally {
    useAuthStore.getState().clearSession();
    usePermStore.getState().clearPerms();
    clearPersistedTokens();
    const suffix = next ? `?next=${encodeURIComponent(next)}` : '';
    navigateTo(`/admin/login${suffix}`, { replace: true });
  }
}

export const adminAuthService = { login, refresh, logout, fetchMe };
